import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { PolicyTrackerService } from "./policy-tracker.service";
import { PolicyTrackerData } from "./policy-tracker.interfaces";
import { PolicyTrackerHeaderComponent } from "./policy-tracker-header/policy-tracker-header.component";
import { PolicyTrackerTimelineComponent } from "./policy-tracker-timeline/policy-tracker-timeline.component";
import { PolicyTrackerInfoComponent } from "./policy-tracker-info/policy-tracker-info.component";
import { normalizeStatus, TrackerState } from "./policy-status.constants";

@Component({
  selector: "app-policy-tracker",
  templateUrl: "./policy-tracker.component.html",
  styleUrls: ["./policy-tracker.component.scss"],
  imports: [
    CommonModule,
    RouterLink,
    MatProgressBarModule,
    PolicyTrackerHeaderComponent,
    PolicyTrackerTimelineComponent,
    PolicyTrackerInfoComponent,
  ],
})
export class PolicyTrackerComponent implements OnInit, OnDestroy {
  private policySubject = new BehaviorSubject<PolicyTrackerData | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private destroy$ = new Subject<void>();

  policy$: Observable<PolicyTrackerData | null> = this.policySubject.asObservable();
  loading$: Observable<boolean> = this.loadingSubject.asObservable();
  error$: Observable<string | null> = this.errorSubject.asObservable();

  // Same branded domain as the tracker; guarded by uploadGuard (branding must allow uploads).
  readonly resubmitUrl = "/upload-coverage-proof";

  constructor(private route: ActivatedRoute, private policyTrackerService: PolicyTrackerService) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const token = params["token"];
      if (token) {
        this.loadPolicyStatus(token);
      } else {
        this.errorSubject.next("No token provided");
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPolicyStatus(token: string): void {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    this.policyTrackerService
      .getPolicyStatus(token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (policy) => {
          this.policySubject.next(policy);
          this.loadingSubject.next(false);
        },
        error: (error) => {
          this.errorSubject.next(error.message || "Failed to load policy status");
          this.loadingSubject.next(false);
        },
      });
  }

  getEffectiveStatus(policy: PolicyTrackerData): string {
    // If latestSubmittedRevision exists, use its revision_status
    const now = new Date().toISOString().slice(0, 10);
    const revision = policy.latestSubmittedRevision?.revision;
    if (revision?.revision_status) {
      // Judge "active yet?" by the revision's own effective date — the policy's date
      // still belongs to the previous submission until the revision is applied. No
      // fallback: a revision without a date is treated as not active yet.
      const effectiveDate = revision.effective_date;
      if ((revision.revision_status === "scheduled" || revision.revision_status === "approved") && effectiveDate && effectiveDate <= now) {
        return "active";
      } else if (revision.revision_status === "scheduled") {
        return "approved";
      }
      return revision.revision_status;
    }
    // Otherwise, use the main policy status
    return policy.status;
  }

  state(policy: PolicyTrackerData): TrackerState {
    return normalizeStatus(this.getEffectiveStatus(policy));
  }

  rejectionReasons(policy: PolicyTrackerData): string[] {
    return policy.rejectionReasons ?? [];
  }

  expirationDate(policy: PolicyTrackerData): string {
    return policy.latestSubmittedRevision?.revision?.expiration_date || policy.expirationDate;
  }
}
